import i18nFactory from '@yandex-int/i18n';
import classNames from 'classnames';
import { Image, Link, Select } from 'lego-on-react';
import { partition } from 'lodash';
import moment from 'moment';
import React, { CSSProperties, ReactElement, useEffect, useRef } from 'react';
import Dropzone, { FileWithPreview } from 'react-dropzone';

import { Shape2, Shape2Icon } from '../../components/Shape2/Shape2';
import { Bem } from '../../service/Bem/Bem';
import { CanvasAppContext, CanvasAppContextProps, ResourceType } from '../common/typings';
import { CreativeOptionGallerySourceType, CreativeOptionSourceType } from '../CreativeOptions/typings';
import { GalleryPaneTypes } from '../GalleryPane/typings';

import './FileEditor.css';
import * as keyset from './FileEditor.i18n';
import { getFileEditorEnabledGalleryTypes } from './helpers';
import { FileEditorOptions, FileEditorStatus, PreviewResource, PreviewResourceType } from './typings';

const i18n = i18nFactory(keyset);

const BLOCK = 'file-editor';

export type FileEditorProps = {
    cls?: string;

    disabled: boolean;
    sources: CreativeOptionSourceType[];
    onValueChange: (value: string | null) => void;

    fileId: string | null;
    fileIdValue: string | null;
    preview?: PreviewResource;
    resourceType: ResourceType;

    groupName: string;
    name: string;

    upload?: boolean;
    longUpload?: boolean;
    hasRecentSource?: boolean;
    hasIdeaSource?: boolean;
    status: FileEditorStatus;
    initFileEditor: (fileId: string | null, options: FileEditorOptions) => void;

    onUploadSourceSelected: (resourceType: ResourceType, files: FileWithPreview[]) => void;
    onUploadCancel: () => void;
    onReset: () => void;
    onCrop: () => void;
    onSmartCenter: () => void;
    onLinkSourceSelected: (urlType: ResourceType) => void;
    onGallerySourceSelected: (
        resourceType: ResourceType,
        galleryType: CreativeOptionGallerySourceType,
        enabledGalleryTypes: GalleryPaneTypes[]
    ) => void;
};

const BUTTON_TEXTS = {
    [FileEditorStatus.NEW]: i18n('add'),
    [FileEditorStatus.EDIT]: i18n('edit'),
    [FileEditorStatus.CONVERTING]: i18n('converting')
};

export const FileEditor = React.memo(function(props: FileEditorProps) {
    const {
        cls,
        fileId,
        fileIdValue,
        disabled,
        preview,
        status,
        sources,
        upload,
        longUpload,
        onUploadCancel,
        initFileEditor,
        resourceType,
        onUploadSourceSelected,
        onReset,
        onLinkSourceSelected,
        onGallerySourceSelected,
        hasRecentSource,
        hasIdeaSource,
        onValueChange,
        groupName,
        name,
        onCrop,
        onSmartCenter
    } = props;

    const isMounted = useRef(true);

    useEffect(() => {
        initFileEditor(fileIdValue, {
            resourceType,
            groupName,
            name
        });
    }, []);

    useEffect(() => {
        if (isMounted.current) {
            isMounted.current = false;
        } else if (fileId !== fileIdValue) {
            onValueChange(fileId);
        }
    }, [fileId]);

    const showUpperContent = disabled || preview;

    return (
        <div className={classNames(BLOCK, cls)}>
            {showUpperContent && (
                <FilePreview preview={preview} disabledReason={getDisabledReason(resourceType)} disabled={disabled} />
            )}
            <FileUploader
                sources={sources}
                hasRecentSource={hasRecentSource}
                hasIdeaSource={hasIdeaSource}
                buttonText={BUTTON_TEXTS[status]}
                onUploadSourceSelected={onUploadSourceSelected}
                onCrop={onCrop}
                onSmartCenter={onSmartCenter}
                onReset={onReset}
                onGallerySourceSelected={onGallerySourceSelected}
                onLinkSourceSelected={onLinkSourceSelected}
                disabled={disabled}
                resetEnabled={Boolean(fileId && sources.includes(CreativeOptionSourceType.reset))}
                upload={upload}
                longUpload={longUpload}
                onUploadCancel={onUploadCancel}
                resourceType={resourceType}
                cropEnabled={Boolean(fileId)}
            />
        </div>
    );
});

const getDisabledReason = (resource: ResourceType): string | undefined => {
    if (resource === ResourceType.audio) {
        return i18n('audio-disable-reason');
    }

    return undefined;
};

type FilePreviewProps = {
    preview?: PreviewResource;
    disabledReason?: string;
    disabled?: boolean;
};

const FilePreview = React.memo((props: FilePreviewProps) => {
    const { preview, disabledReason, disabled } = props;
    const showDisableDisclaimer = disabled && disabledReason;

    return (
        <div className={Bem.cls(BLOCK, 'preview')}>
            {showDisableDisclaimer && <div className={Bem.cls(BLOCK, 'preview-name')}>{disabledReason}</div>}

            {preview && !showDisableDisclaimer && (
                <>
                    {preview.type === PreviewResourceType.AUDIO && (
                        <>
                            <div className={Bem.cls(BLOCK, 'preview-name')}>{preview.name}</div>
                            <div className={Bem.cls(BLOCK, 'preview-duration')}>
                                {moment(preview.duration * 1000).format('mm:ss')}
                            </div>
                        </>
                    )}
                    {preview.type === PreviewResourceType.IMAGE && (
                        <Image cls={Bem.cls(BLOCK, 'preview-image')} url={preview.previewUrl} />
                    )}
                    {preview.type === PreviewResourceType.OVERLAY && (
                        <div className={Bem.cls(BLOCK, 'preview-name')}>{preview.name}</div>
                    )}
                </>
            )}
        </div>
    );
});

type FileUploaderProps = {
    sources: CreativeOptionSourceType[];
    buttonText: string;
    resetEnabled: boolean;
    cropEnabled: boolean;
    upload?: boolean;
    longUpload?: boolean;
    disabled: boolean;
    resourceType: ResourceType;
    hasRecentSource?: boolean;
    hasIdeaSource?: boolean;

    onCrop: () => void;
    onSmartCenter: () => void;
    onUploadSourceSelected: (resourceType: ResourceType, files: FileWithPreview[]) => void;
    onUploadCancel: () => void;
    onReset: () => void;
    onLinkSourceSelected: (urlType: ResourceType) => void;
    onGallerySourceSelected: (
        resourceType: ResourceType,
        galleryType: CreativeOptionGallerySourceType,
        enabledGalleryTypes: GalleryPaneTypes[]
    ) => void;
};

type FileUploaderState = { dragging: boolean; selectVal: string };

const SOURCE_KEYS = {
    [CreativeOptionSourceType.idea]: 'idea-source',
    [CreativeOptionSourceType.link]: 'link-source',
    [CreativeOptionSourceType.stock]: 'library-source',
    [CreativeOptionSourceType.recent]: 'recent-source',
    [CreativeOptionSourceType.upload]: 'upload-source',
    [CreativeOptionSourceType.crop]: 'crop',
    [CreativeOptionSourceType.smartCenter]: 'smart-center'
};

const POSITION_ABSOLUTE: CSSProperties = { position: 'absolute' };

export class FileUploader extends React.Component<FileUploaderProps, FileUploaderState> {
    state = { dragging: false, selectVal: '' };
    private dropzone: React.RefObject<Dropzone> & React.RefObject<HTMLDivElement> = React.createRef();
    private draggingTimeout: number;

    componentDidMount(): void {
        document.addEventListener('dragenter', this.onDragEnter);
        document.addEventListener('dragover', this.onDragEnter);
        document.addEventListener('dragleave', this.onDragLeave);
    }

    componentWillUnmount(): void {
        document.removeEventListener('dragenter', this.onDragEnter);
        document.removeEventListener('dragover', this.onDragEnter);
        document.removeEventListener('dragleave', this.onDragLeave);
    }

    render() {
        const { disabled, buttonText, resetEnabled, upload, longUpload, onUploadCancel, onReset } = this.props;

        return (
            <div className={Bem.cls(BLOCK, 'uploader')}>
                <div
                    className={Bem.cls(BLOCK, 'uploader-controls', {
                        dragging: this.state.dragging ? 'active' : ''
                    })}
                >
                    <Dropzone
                        ref={this.dropzone}
                        className={Bem.cls(BLOCK, 'uploader-dropzone')}
                        style={POSITION_ABSOLUTE}
                        onDrop={this.onFileDrop}
                        disabled={disabled}
                    >
                        <div />
                    </Dropzone>
                    <CanvasAppContext.Consumer>
                        {({ parent }: CanvasAppContextProps) => (
                            <Select
                                cls={Bem.cls(BLOCK, 'uploader-select', {
                                    progress: upload
                                })}
                                theme={'normal'}
                                type={'radio'}
                                size={'xs'}
                                disabled={disabled || upload}
                                placeholder={buttonText}
                                val={this.state.selectVal}
                                onChange={this.onSourceSelected}
                                popup={{
                                    scope: parent || undefined
                                }}
                            >
                                {this.renderOptions()}
                            </Select>
                        )}
                    </CanvasAppContext.Consumer>
                </div>

                {resetEnabled && (
                    <Shape2
                        cls={Bem.cls(BLOCK, 'uploader-reset-icon')}
                        icon={Shape2Icon.trash}
                        width={14}
                        height={14}
                        onClick={onReset}
                    />
                )}

                {longUpload && <CancelLink onClick={onUploadCancel} />}
            </div>
        );
    }

    private onDragEnter = (e: DragEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (e.dataTransfer && this.isFilesDragging(e.dataTransfer)) {
            this.setState({ dragging: true });
            this.setDraggingTimeout();
        }
    };

    private onDragLeave = (e: DragEvent) => {
        e.preventDefault();

        if (e.target === document) {
            this.setState({ dragging: false });
        }
    };

    private setDraggingTimeout(): void {
        if (this.draggingTimeout) {
            clearTimeout(this.draggingTimeout);
        }
        this.draggingTimeout = window.setTimeout(() => {
            this.setState({ dragging: false });
        }, 200);
    }

    private isFilesDragging(data: DataTransfer): boolean {
        return data.types.includes('Files') || data.types.includes('application/x-moz-file');
    }

    private onFileDrop = (accepted: FileWithPreview[], rejected: FileWithPreview[]) => {
        this.props.onUploadSourceSelected(this.props.resourceType, accepted.concat(rejected));
    };

    private onSourceSelected = (value: string[]) => {
        const {
            resourceType,
            sources,
            onLinkSourceSelected,
            onReset,
            onGallerySourceSelected,
            onCrop,
            onSmartCenter
        } = this.props;

        const option = value[0];
        switch (option) {
            case CreativeOptionSourceType.upload:
                if (this.dropzone.current) {
                    this.dropzone.current.open();
                }
                break;

            case CreativeOptionSourceType.link:
                onLinkSourceSelected(resourceType);
                break;

            case CreativeOptionSourceType.reset:
                onReset();
                break;

            case CreativeOptionSourceType.recent:
            case CreativeOptionSourceType.stock:
            case CreativeOptionSourceType.idea:
                onGallerySourceSelected(resourceType, option, getFileEditorEnabledGalleryTypes(sources));
                break;

            case CreativeOptionSourceType.crop:
                onCrop();
                break;

            case CreativeOptionSourceType.smartCenter:
                onSmartCenter();
                break;

            default:
                break;
        }

        // Спецкостыль, чтобы можно было использовать Select
        this.setState({ selectVal: '' });
    };

    private renderOptions(): React.ReactNode {
        const { sources, hasRecentSource, hasIdeaSource, cropEnabled } = this.props;

        const filteredSources = this.getSources(sources, hasRecentSource, hasIdeaSource, cropEnabled);
        const selectOptions = [];
        const [firstGroup, restSources] = partition(
            filteredSources,
            s =>
                s === CreativeOptionSourceType.upload ||
                s === CreativeOptionSourceType.crop ||
                s === CreativeOptionSourceType.smartCenter
        );

        if (firstGroup.length) {
            selectOptions.push(this.renderSelectGroup(1, firstGroup));
        }

        if (restSources.length > 0) {
            selectOptions.push(this.renderSelectGroup(2, restSources));
        }

        return selectOptions;
    }

    private getSources(
        sources: CreativeOptionSourceType[],
        hasRecentSource?: boolean,
        hasIdeaSource?: boolean,
        cropEnabled?: boolean
    ): CreativeOptionSourceType[] {
        return sources.filter(
            s =>
                s !== CreativeOptionSourceType.reset &&
                (hasRecentSource || s !== CreativeOptionSourceType.recent) &&
                (hasIdeaSource || s !== CreativeOptionSourceType.idea) &&
                (cropEnabled || s !== CreativeOptionSourceType.crop) &&
                (cropEnabled || s !== CreativeOptionSourceType.smartCenter)
        );
    }

    private renderSelectGroup(groupKey: number, sources: CreativeOptionSourceType[]): ReactElement {
        return (
            <Select.Group key={groupKey}>
                {sources.map(s => (
                    <Select.Item key={s} val={s}>
                        {i18n(SOURCE_KEYS[s])}
                    </Select.Item>
                ))}
            </Select.Group>
        );
    }
}

const CancelLink = React.memo((props: { onClick: () => void }) => (
    <Link cls={Bem.cls(BLOCK, 'cancel-link')} theme={'normal'} pseudo={true} inner={true} onClick={props.onClick}>
        <span className={Bem.cls(BLOCK, 'cancel-link-text')}>Отменить</span>
        <Shape2 cls={Bem.cls(BLOCK, 'cancel-link-icon')} width={14} height={14} icon={Shape2Icon.closeBadge} />
    </Link>
));
